import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useSelector } from '@store';
import {
  CapaQuotationChatRoomSharedFiles,
  CapaQuotationChatRoomSharedFilesFilterType,
  useFetchQuotationChatRoomSharedFilesQuery,
  useFetchQuotationUserRfqDetailQuery
} from '@generated/graphql';
import { ViewerMode } from './types';
import ViewerDownloadSnackbar from './ViewerDownloadSnackbar';
import { getExtension, getFileType } from './utils';
import ViewerPresenter from './ViewerPresenter';
import useViewerTab from './useViewerTab';

export type TabValue = 'ALL' | 'REQUEST' | 'CHAT';

interface Props {
  rfqId: string;
  chatRoomId: string;
  mode: ViewerMode;
}

const deleteSelectedFileId = () => {
  if (window.location.search.includes('selectedFileId')) {
    // delete selectedFileId param. because this param is used for first render only.
    const url = new URL(window.location.href);
    url.searchParams.delete('selectedFileId');

    // update url without refresh.
    window.history.pushState(null, '', url.toString());
  }
};

const ViewerContainer = ({ chatRoomId, mode, rfqId }: Props) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, onChangeTab] = useViewerTab({
    callback: () => {
      setIndex(0);
    }
  });
  const [index, setIndex] = useState<number>(0);
  const [rightDrawer, setRightDrawer] = useState(false);
  const [downloadFile, setDownloadFile] = useState<
    CapaQuotationChatRoomSharedFiles | undefined
  >(undefined);
  const request = useSelector((state) => state?.request);
  const isPreview = !rfqId && !chatRoomId;
  const canDownload = !(isPreview && !request?.publicOpen);

  // 채팅룸에 관련된 파일 리스트(RFQ 도면파일, 채팅방 공유파일)
  const {
    data: filesData,
    refetch: refetchFiles
  } = useFetchQuotationChatRoomSharedFilesQuery({
    variables: {
      input: {
        chatRoomId: (chatRoomId as unknown) as string,
        filterType:
          tab === 'ALL'
            ? CapaQuotationChatRoomSharedFilesFilterType.AllFile
            : tab === 'CHAT'
            ? CapaQuotationChatRoomSharedFilesFilterType.ChatMessageFile
            : CapaQuotationChatRoomSharedFilesFilterType.QuotationFile
      }
    },
    fetchPolicy: 'network-only',
    skip: !chatRoomId || isPreview
  });

  // RFQ 데이터(채팅 외 RFQ 상세를 확인할 때)
  const { data: rfqData } = useFetchQuotationUserRfqDetailQuery({
    variables: { rfqId: rfqId as string },
    fetchPolicy: 'cache-and-network',
    skip: !rfqId
  });

  const presentFiles = useMemo(() => {
    const previewDrawingFiles = request?.uploadedDrawingFiles ?? [];
    const previewReferenceFiles = request?.uploadedReferenceFiles ?? [];
    const previewPcbFabricationFiles = request?.pcb?.fabricationFiles ?? [];
    const previewPcbAssemblyFiles = request?.pcb?.assemblyFiles ?? [];
    const fabricationFiles =
      rfqData?.fetchQuotationUserRfqDetail?.data?.pcb?.fabricationFiles ?? [];
    const drawingFiles =
      rfqData?.fetchQuotationUserRfqDetail?.data?.drawingFiles ?? [];
    const referenceFiles =
      rfqData?.fetchQuotationUserRfqDetail?.data?.referenceFiles ?? [];

    if (isPreview) {
      return [
        ...previewDrawingFiles,
        ...previewPcbFabricationFiles,
        ...previewPcbAssemblyFiles,
        ...previewReferenceFiles
      ];
    } else {
      return [...drawingFiles, ...fabricationFiles, ...referenceFiles];
    }
  }, [request, rfqData, isPreview]);

  const files = useMemo(() => {
    if (chatRoomId) {
      return filesData?.fetchQuotationChatRoomSharedFiles?.data ?? [];
    } else {
      return presentFiles.map(
        (file: {
          fileId?: string;
          fileName?: string | null;
          fileSize?: number | null;
        }) =>
          ({
            fileId: file.fileId || '',
            fileName: file.fileName || '',
            quotationSharedFileSize: file.fileSize || 0
          } as CapaQuotationChatRoomSharedFiles)
      );
    }
  }, [chatRoomId, presentFiles, filesData]);

  const extension = getExtension(files[index]?.fileName ?? '');
  const fileType = getFileType(extension);
  const currentItem = files.length !== 0 ? files[index] : undefined;
  const isFirstItem = index === 0;
  const isLastItem = index + 1 === files.length;

  const onHandleRightDrawer = (value: boolean) => setRightDrawer(value);

  const onChangeIndex = useCallback(
    (_index: number, type?: 'add' | 'sub') => {
      setRightDrawer(false);

      if (type === 'add') {
        return setIndex((prev) => {
          if (prev === files.length) {
            return prev;
          }

          return prev + 1;
        });
      }

      if (type === 'sub') {
        return setIndex((prev) => {
          if (prev === 0) {
            return prev;
          } else {
            return prev - 1;
          }
        });
      }

      setIndex(_index);
    },
    [files.length]
  );

  const onDownload = useCallback(
    async (file?: CapaQuotationChatRoomSharedFiles) => {
      if (!canDownload || downloadFile) return;

      setDownloadFile(file);
    },
    [downloadFile, canDownload]
  );

  const onDownloadComplete = useCallback(() => setDownloadFile(undefined), []);

  useEffect(() => {
    if (!files.length) return;
    const selectedFileId = new URLSearchParams(window.location.search).get(
      'selectedFileId'
    );

    // 진입 시 첫번째 아이템 지정
    if (selectedFileId) {
      const selectedFileIndex = files.findIndex(
        (file) => file.fileId === selectedFileId
      );
      if (selectedFileIndex === -1) return;

      onChangeIndex(selectedFileIndex);

      deleteSelectedFileId();
    }
  }, [files, onChangeIndex]);

  return (
    <>
      {canDownload && (
        <ViewerDownloadSnackbar
          currentItem={downloadFile}
          onComplete={onDownloadComplete}
        />
      )}

      <ViewerPresenter
        mode={mode}
        matches={matches}
        files={files}
        tab={tab}
        rightDrawer={rightDrawer}
        currentItem={currentItem}
        fileCategory={fileType}
        isFirstItem={isFirstItem}
        isLastItem={isLastItem}
        canDownload={canDownload ?? false}
        onHandleRightDrawer={onHandleRightDrawer}
        onChangeTab={onChangeTab}
        onChangeIndex={onChangeIndex}
        onDownload={onDownload}
        refetchFiles={refetchFiles}
      />
    </>
  );
};

export default ViewerContainer;
